/**
 * StreamingResponse Component - Display streaming search results
 * TASK-270: Unified Search Interface
 */

import { Loader2, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SourceList } from './SourceAttribution';
import type { SearchResponse, SearchStatus, SourceAttribution } from '../../types/search.types';

interface StreamingResponseProps {
  // For non-streaming mode
  response?: SearchResponse | null;

  // For streaming mode
  partialAnswer?: string;
  streamingSources?: SourceAttribution[];

  // Shared state
  status: SearchStatus | null;
  isSearching: boolean;
  error?: Error | null;

  // Callbacks
  onSourceClick?: (source: SourceAttribution) => void;
  onFollowUpClick?: (query: string) => void;

  className?: string;
}

export function StreamingResponse({
  response,
  partialAnswer = '',
  streamingSources = [],
  status,
  isSearching,
  error,
  onSourceClick,
  onFollowUpClick,
  className,
}: StreamingResponseProps) {
  // Determine content to show
  const answer = response?.answer || partialAnswer;
  const sources = response?.sources || streamingSources;
  const followUps = response?.followUpSuggestions || [];

  // Show nothing if no content
  if (!isSearching && !answer && !error) {
    return null;
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Status Indicator */}
      {isSearching && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Searching across your data...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Search failed</p>
            <p className="text-sm text-red-600 mt-1">{error.message}</p>
          </div>
        </div>
      )}

      {/* Answer Card */}
      {answer && (
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          {/* Answer Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-gray-50">
            {status === 'completed' ? (
              <CheckCircle2 aria-label="Search completed" className="h-4 w-4 text-green-500" />
            ) : status === 'partial' ? (
              <AlertCircle className="h-4 w-4 text-yellow-500" />
            ) : (
              <Sparkles className="h-4 w-4 text-blue-500" />
            )}
            <span className="text-sm font-medium text-gray-700">Answer</span>
          </div>

          {/* Answer Content */}
          <div className="p-4">
            <div className="prose prose-sm max-w-none text-gray-700">
              {answer}
              {isSearching && (
                <span className="inline-block w-1.5 h-4 bg-blue-500 ml-0.5 animate-pulse" />
              )}
            </div>
          </div>

          {/* TASK-605: Confidence/Intent/ms are logging metadata, not UI — they
              stay in the API payload; only real failures surface here. */}
          {response && response.failedSources.length > 0 && (
            <div className="px-4 py-2 border-t bg-gray-50 text-xs text-yellow-600">
              {response.failedSources.length} source(s) failed
            </div>
          )}
        </div>
      )}

      {/* Sources */}
      {sources.length > 0 && (
        <SourceList sources={sources} onSourceClick={onSourceClick} maxVisible={4} />
      )}

      {/* TASK-605: empty results suggest scoped retries instead of a dead end. */}
      {!isSearching && response && sources.length === 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Nothing matched — try a scoped search</h4>
          <div className="flex flex-wrap gap-2">
            {['clients', 'documents', 'meetings'].map((scope) => (
              <button
                key={scope}
                onClick={() => onFollowUpClick?.(`Search within ${scope}: ${response.query}`)}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-full border transition-all',
                  'bg-white border-gray-200 text-gray-600',
                  'hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700'
                )}
              >
                Search within {scope}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Follow-up Suggestions */}
      {followUps.length > 0 && !isSearching && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Follow-up questions</h4>
          <div className="flex flex-wrap gap-2">
            {followUps.map((query, index) => (
              <button
                key={index}
                onClick={() => onFollowUpClick?.(query)}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-full border transition-all',
                  'bg-white border-gray-200 text-gray-600',
                  'hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700'
                )}
              >
                {query}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Warnings */}
      {response?.warnings && response.warnings.length > 0 && (
        <div className="text-xs text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          {response.warnings.map((warning, index) => (
            <p key={index}>{warning}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default StreamingResponse;
